// ReportHandler.js
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import { apiClient } from '../AI/ai-utils';
import { 
  createReportSubmissionNotification, 
  createReportOutcomeNotification, 
  createContentRemovedNotification,
  createAppealNotificationForAdmin 
} from '../../utils/notificationUtils';

/**
 * Submit a report to Firebase
 * @param {string} contentId - ID of the reported content
 * @param {string} contentType - Type of content: 'query', 'comment', or 'reply'
 * @param {string} reportedText - The text of the reported content
 * @param {string} reportedUserId - User ID of the person who created the reported content
 * @param {string} reporterId - User ID of the reporter
 * @param {string} reason - Reason for the report
 * @param {string} [parentId] - For replies, the parent comment ID; for comments, the query ID
 * @param {Object} [additionalData] - Any additional data to include
 */
export const submitReport = async (
  contentId,
  contentType,
  reportedText,
  reportedUserId,
  reporterId,
  reason,
  parentId = null,
  additionalData = {}
) => {
  try {
    // Create the report in Firestore
    const reportRef = await addDoc(collection(db, 'reports'), {
      contentId,
      contentType,
      reportedText,
      reportedUserId,
      reporterId,
      reason,
      parentId,
      status: 'pending', // 'pending', 'approved', 'rejected'
      createdAt: serverTimestamp(),
      aiAnalysis: null,
      aiDecision: null,
      ...additionalData
    });

    // Send notification to the reporter
    await createReportSubmissionNotification(reporterId, contentType);
    
    // Use the AI to analyze and make a decision
    await analyzeReportWithAI(reportRef.id);
    
    return { success: true, reportId: reportRef.id };
  } catch (error) {
    console.error('Error submitting report:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Process a report with AI
 * @param {string} reportId - The ID of the report to analyze
 */
export const analyzeReportWithAI = async (reportId) => {
  try {
    const reportDoc = await getDoc(doc(db, 'reports', reportId));
    
    if (!reportDoc.exists()) {
      throw new Error('Report not found');
    }
    
    const reportData = reportDoc.data();
    let aiAnalysis = null;
    let aiDecision = null;
    
    try {
      // First try the AI endpoint
      const response = await apiClient.post('/api/ai/analyze-report', {
        reportText: reportData.reportedText,
        contentType: reportData.contentType,
        reportReason: reportData.reason
      });
      
      // Check if the response has the expected format
      if (response.data && response.data.shouldRemove !== undefined) {
        // Convert the AI response format to our internal format
        aiAnalysis = {
          recommendation: response.data.shouldRemove ? 'remove' : 'keep',
          confidence: response.data.confidence || 0.5,
          reasoning: response.data.reason || 'Analysis complete',
          categoryViolated: response.data.categoryViolated
        };
        
        // Determine the decision based on AI recommendation and confidence
        aiDecision = response.data.shouldRemove && response.data.confidence > 0.6 ? 'remove' : 'keep';
      }
    } catch (error) {
      console.warn('AI analysis endpoint failed, using fallback logic:', error.message);
      
      // Fallback logic - check for keywords in the reported text
      const reportTextLower = reportData.reportedText.toLowerCase();
      const containsProhibitedContent = 
        reportTextLower.includes('offensive') || 
        reportTextLower.includes('hate') || 
        reportTextLower.includes('spam') ||
        reportTextLower.includes('inappropriate') ||
        reportData.reason === 'harassment' ||
        reportData.reason === 'hate_speech';
      
      // Simple decision logic
      const shouldRemove = containsProhibitedContent;
      
      aiAnalysis = {
        recommendation: shouldRemove ? 'remove' : 'keep',
        confidence: shouldRemove ? 0.7 : 0.6,
        reasoning: shouldRemove ? 'Content appears to violate community guidelines' : 'Content does not appear to violate guidelines',
        categoryViolated: shouldRemove ? (reportData.reason || 'general_violation') : null
      };
      
      aiDecision = shouldRemove ? 'remove' : 'keep';
    }
    
    // Update the report with AI analysis
    await updateDoc(doc(db, 'reports', reportId), {
      aiAnalysis,
      aiDecision,
      status: aiDecision === 'remove' ? 'approved' : 'rejected',
      processedAt: serverTimestamp()
    });
    
    // If the decision is to remove content, handle the removal
    if (aiDecision === 'remove') {
      await handleContentRemoval(reportData);
    }
    
    // Notify the reporter about the outcome
    if (reportData.reporterId) {
      await createReportOutcomeNotification(
        reportData.reporterId, 
        reportData.contentType, 
        aiDecision === 'remove',
        reportData.contentId,
        reportData.parentId
      );
    }
    
    return {
      success: true,
      decision: aiDecision,
      analysis: aiAnalysis
    };
  } catch (error) {
    console.error('Error analyzing report with AI:', error);
    throw error;
  }
};

/**
 * Create a notification for admin about a report
 * @param {string} adminId - Admin's user ID
 * @param {string} contentType - Type of reported content
 * @param {string} reportedText - Text that was reported
 * @param {string} reportId - ID of the report
 */
const createNotificationForAdmin = async (adminId, contentType, reportedText, reportId) => {
  try {
    const truncatedText = reportedText.length > 50 
      ? reportedText.substring(0, 50) + '...' 
      : reportedText;
    
    const message = `A ${contentType} was automatically removed: "${truncatedText}"`;
    const link = `/admin/reports/${reportId}`;
    
    await addDoc(collection(db, 'notifications'), {
      userId: adminId,
      message,
      type: 'moderation',
      link,
      read: false,
      createdAt: serverTimestamp(),
      requiresAction: false, // No action required since AI already removed it
      reportId
    });
  } catch (error) {
    console.error('Error notifying admin:', error);
  }
};

/**
 * Handle content removal based on the type
 * @param {Object} reportData - The report data
 */
const handleContentRemoval = async (reportData) => {
  const { contentType, contentId, parentId, reportedUserId } = reportData;
  let queryId = null;
  
  try {
    switch (contentType) {
      case 'query':
        // Mark query as hidden/reported instead of deleting
        await updateDoc(doc(db, 'queries', contentId), {
          isHidden: true,
          isRemoved: true, // Explicitly mark as removed
          reportReason: 'Violated community guidelines',
          reportedAt: serverTimestamp()
        });
        queryId = contentId;
        break;
        
      case 'comment':
        // Mark comment as hidden/reported
        await updateDoc(doc(db, 'queries', parentId, 'comments', contentId), {
          isHidden: true,
          isRemoved: true, // Explicitly mark as removed
          reportReason: 'Violated community guidelines',
          reportedAt: serverTimestamp()
        });
        queryId = parentId;
        break;
        
      case 'reply':
        // Mark reply as hidden/reported
        const commentId = parentId; // For replies, parentId is the comment ID
        queryId = reportData.queryId; // We need to pass this in additionalData
        
        await updateDoc(doc(db, 'queries', queryId, 'comments', commentId, 'replies', contentId), {
          isHidden: true,
          isRemoved: true, // Explicitly mark as removed
          reportReason: 'Violated community guidelines',
          reportedAt: serverTimestamp()
        });
        break;
        
      default:
        console.error('Unknown content type:', contentType);
    }
    
    // Notify the user whose content was removed - ensure this is called
    if (reportedUserId) {
      console.log(`Sending removal notification to user: ${reportedUserId}`);
      await createContentRemovedNotification(
        reportedUserId, 
        contentType, 
        queryId, 
        'Violated community guidelines'
      );
    } else {
      console.error('No reportedUserId provided for content removal notification');
    }
    
    return true;
  } catch (error) {
    console.error('Error handling content removal:', error);
    return false;
  }
};

/**
 * Submit an appeal for removed content
 * @param {string} contentId - ID of the content
 * @param {string} contentType - Type of content
 * @param {string} userId - User ID of the person appealing
 * @param {string} reason - Reason for the appeal
 * @param {string} [queryId] - ID of the parent query
 */
export const submitAppeal = async (contentId, contentType, userId, reason, queryId = null) => {
  try {
    // Create the appeal in Firestore
    const appealRef = await addDoc(collection(db, 'appeals'), {
      contentId,
      contentType,
      userId,
      reason,
      queryId,
      status: 'pending', // 'pending', 'approved', 'rejected'
      createdAt: serverTimestamp(),
      reviewedAt: null,
      reviewedBy: null
    });

    // Notify admins about the appeal
    const adminSnapshot = await getDocs(
      query(collection(db, 'users'), where('isAdmin', '==', true))
    );
    
    // Notify all admins
    adminSnapshot.forEach(doc => {
      const adminId = doc.id;
      createAppealNotificationForAdmin(adminId, contentType, appealRef.id);
    });
    
    return { success: true, appealId: appealRef.id };
  } catch (error) {
    console.error('Error submitting appeal:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a ReportModal component to be reused across the application
 */
export const getReportReasons = () => {
  return [
    'Inappropriate content',
    'Hate speech',
    'Harassment',
    'Misinformation',
    'Spam',
    'Violates community guidelines',
    'Other'
  ];
}; 